/**
 * bg-removal-slider.js
 * Ashe Design – before/after compare slider at the top of the Remove
 * Background page.
 *
 * - Starts on the example photos from Theme settings → Background removal.
 *   (If none are set, the slider stays hidden until the customer's first result.)
 * - After a customer removes a background, shows THEIR photo and result.
 * - "Remove another" puts the examples back.
 * - Drag with a mouse or finger. Arrow keys work too (hidden range input).
 *
 * Works with bg-removal-tool.js through two events on [data-bgr-root]:
 *   "bgr:result"  detail: { beforeUrl, afterUrl }  → show the customer's images
 *   "bgr:reset"                                    → back to the examples
 */
(() => {
  'use strict';

  const START_POS = 50;

  const clamp = (n) => Math.min(100, Math.max(0, n));
  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initSlider(root, box) {
    if (box.dataset.bgrReady === 'true') return;
    box.dataset.bgrReady = 'true';

    const frame = box.querySelector('[data-bgr-slider-frame]');
    const beforeImg = box.querySelector('[data-bgr-slider-before]');
    const afterImg = box.querySelector('[data-bgr-slider-after]');
    const range = box.querySelector('[data-bgr-slider-range]');
    if (!frame || !beforeImg || !afterImg) {
      console.error('[bg-removal-slider] Slider markup is incomplete.');
      return;
    }

    const examples = {
      before: box.dataset.exampleBefore || '',
      after: box.dataset.exampleAfter || '',
    };
    const hasExamples = Boolean(examples.before && examples.after);
    let dragging = false;

    // ---- position ----
    function setPos(value) {
      const number = Number(value);
      if (!Number.isFinite(number)) return;
      const pos = clamp(number);
      frame.style.setProperty('--bgr-pos', `${pos}%`);
      if (range && Number(range.value) !== Math.round(pos)) range.value = String(Math.round(pos));
    }

    function posFromPointer(event) {
      const rect = frame.getBoundingClientRect();
      if (!rect.width) return START_POS;
      return ((event.clientX - rect.left) / rect.width) * 100;
    }

    // ---- sizing: match the frame to the photo's shape ----
    function fitFrameTo(img) {
      if (img.naturalWidth && img.naturalHeight) {
        frame.style.aspectRatio = `${img.naturalWidth} / ${img.naturalHeight}`;
      }
    }
    beforeImg.addEventListener('load', () => fitFrameTo(beforeImg));
    if (beforeImg.complete) fitFrameTo(beforeImg);

    // ---- swapping images ----
    function showImages(beforeUrl, afterUrl, isCustomer) {
      beforeImg.src = beforeUrl;
      afterImg.src = afterUrl;
      // Width/height from the examples would squash a different-shaped photo.
      beforeImg.removeAttribute('width');
      beforeImg.removeAttribute('height');
      afterImg.removeAttribute('width');
      afterImg.removeAttribute('height');
      beforeImg.alt = isCustomer ? 'Your original photo' : 'Example photo before';
      afterImg.alt = isCustomer ? 'Your photo with the background removed' : 'Example photo with the background removed';
      box.classList.toggle('is-customer', isCustomer);
      setPos(START_POS);
      box.hidden = false;
    }

    function showExamplesOrHide() {
      if (hasExamples) {
        showImages(examples.before, examples.after, false);
        return;
      }
      box.hidden = true;
      beforeImg.removeAttribute('src');
      afterImg.removeAttribute('src');
    }

    // On phones the slider sits below the upload card, so bring it into view.
    function scrollIntoViewIfNeeded() {
      const rect = box.getBoundingClientRect();
      const offScreen = rect.top > window.innerHeight * 0.6 || rect.bottom < 0;
      if (offScreen) {
        box.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
      }
    }

    // ---- dragging (mouse, finger, pen) ----
    frame.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      dragging = true;
      try {
        frame.setPointerCapture(event.pointerId);
      } catch {
        // Older browsers: dragging still works while the pointer stays on the frame.
      }
      setPos(posFromPointer(event));
    });
    frame.addEventListener('pointermove', (event) => {
      if (dragging) setPos(posFromPointer(event));
    });
    ['pointerup', 'pointercancel', 'lostpointercapture'].forEach((type) =>
      frame.addEventListener(type, () => {
        dragging = false;
      })
    );

    // ---- keyboard (arrow keys on the hidden range input) ----
    if (range) {
      range.addEventListener('input', () => setPos(range.value));
    }

    // ---- events from the removal tool ----
    root.addEventListener('bgr:result', (event) => {
      const detail = event.detail || {};
      if (!detail.beforeUrl || !detail.afterUrl) return;
      showImages(detail.beforeUrl, detail.afterUrl, true);
      scrollIntoViewIfNeeded();
    });
    root.addEventListener('bgr:reset', showExamplesOrHide);

    setPos(START_POS);
  }

  function boot(scope = document) {
    scope.querySelectorAll('[data-bgr-root]').forEach((root) => {
      root.querySelectorAll('[data-bgr-slider]').forEach((box) => {
        try {
          initSlider(root, box);
        } catch (error) {
          console.error('[bg-removal-slider] Startup failed:', error);
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }
})();
